import * as React from 'react';
import { useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Avatar,
  TextField,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { getTenantApiUrl } from './utils/tenant';
import OtpVerification from './OtpVerification';

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [resetStep, setResetStep] = useState('request'); // 'request' or 'reset'
    const [resetEmail, setResetEmail] = useState('');
    const [passPhrase, setPassPhrase] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showOtpVerification, setShowOtpVerification] = useState(false);
    const [otpMessage, setOtpMessage] = useState('');
    const [otpUsername, setOtpUsername] = useState('');
    const login = useLogin();
    const notify = useNotify();

    const handleSubmit = async e => {
        e.preventDefault();
        // Validate fields
        if (!username.trim() || !password.trim()) {
            notify('Username and password are required');
            return;
        }

        try {
            await login({ username, password });
        } catch (error) {
            // Check if OTP verification is required
            if (error && error.requiresVerification) {
                setOtpMessage(error.message || "We've sent a verification code to your email because you're logging in from a new location.");
                setOtpUsername(error.username || username);
                setShowOtpVerification(true);
            } else {
                notify(error?.message || 'Invalid username or password');
            }
        }
    };

    const handleOtpVerify = async (verifyUsername, otp) => {
        try {
            await login({ username: verifyUsername, otp });
            // Success - login will handle the redirect
        } catch (error) {
            // Re-throw the error to be handled by OtpVerification component
            throw error;
        }
    };

    const handleOtpBack = () => {
        setShowOtpVerification(false);
        setOtpMessage('');
        setOtpUsername('');
        setPassword(''); // Clear password for security
    };

    const handleRequestPasswordReset = async () => {
        if (!resetEmail.trim()) {
            notify('Email address is required');
            return;
        }

        setIsLoading(true);
        try {
            const apiUrl = getTenantApiUrl('auth/request-password-reset');
            console.log('Password reset request URL:', apiUrl);
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username: resetEmail }),
            });

            if (response.ok) {
                notify('Password reset request sent. Please check your email for the pass-phrase.');
                setResetStep('reset');
            } else {
                const errorData = await response.text();
                console.error('Password reset error:', response.status, errorData);
                notify(`Failed to send password reset request: ${response.status}`);
            }
        } catch (error) {
            console.error('Network error during password reset:', error);
            notify(`Network error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        if (!passPhrase.trim() || !newPassword.trim() || !confirmPassword.trim()) {
            notify('All fields are required');
            return;
        }

        if (newPassword !== confirmPassword) {
            notify('Passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            const apiUrl = getTenantApiUrl('auth/reset-password');
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: resetEmail,
                    passphrase: passPhrase,
                    newPassword: newPassword
                }),
            });

            if (response.ok) {
                notify('Password reset successful. You can now login with your new password.');
                handleCloseResetDialog();
            } else {
                const errorData = await response.text();
                console.error('Password reset error:', response.status, errorData);
                notify(`Failed to reset password: ${errorData || 'Please check your pass-phrase and try again.'}`);
            }
        } catch (error) {
            notify('Network error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenResetDialog = () => {
        setResetEmail(username);
        setResetDialogOpen(true);
        setResetStep('request');
    };

    const handleCloseResetDialog = () => {
        setResetDialogOpen(false);
        setResetStep('request');
        setResetEmail('');
        setPassPhrase('');
        setNewPassword('');
        setConfirmPassword('');
        setIsLoading(false);
    };

    // Show OTP verification screen if needed
    if (showOtpVerification) {
        return (
            <OtpVerification
                username={otpUsername}
                message={otpMessage}
                onVerify={handleOtpVerify}
                onBack={handleOtpBack}
            />
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: 'url(/wygc-bg.svg)',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                backgroundColor: 'rgba(255, 255, 255, 0)',
                backgroundBlendMode: 'lighten'
            }}
        >
            <Card sx={{ minWidth: 300, maxWidth: 400, boxShadow: 3 }}>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                    p={3}
                    bgcolor="primary.main"
                    color="primary.contrastText"
                >
                    <Avatar 
                        src="/wygc-logo.png" 
                        alt="WYGC Logo" 
                        sx={{ 
                            width: 80, 
                            height: 80,
                            border: '2px solid white',
                            boxShadow: 2,
                            mb: 2,
                            bgcolor: 'white',
                            p: 1
                        }} 
                    />
                    <Typography variant="h5" component="h1">
                        WhoYouGonnaCall?
                    </Typography>
                </Box>

                <CardContent>
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                        <TextField
                            label="Username"
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            required
                            fullWidth
                            autoFocus
                            margin="normal"
                            variant="outlined"
                        />
                        <TextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            fullWidth
                            margin="normal"
                            variant="outlined"
                        />
                        <CardActions sx={{ justifyContent: 'center', mt: 2, flexDirection: 'column' }}>
                            <Button
                                variant="contained"
                                type="submit"
                                color="primary"
                                fullWidth
                                size="large"
                            >
                                Login
                            </Button>
                            <Link
                                component="button"
                                variant="body2"
                                onClick={handleOpenResetDialog}
                                sx={{ mt: 1, cursor: 'pointer' }}
                            >
                                Reset my password
                            </Link>
                        </CardActions>
                    </Box>
                </CardContent>
            </Card>

            {/* Password Reset Dialog */}
            <Dialog 
                open={resetDialogOpen} 
                onClose={handleCloseResetDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {resetStep === 'request' ? 'Reset Password' : 'Enter New Password'}
                </DialogTitle>
                
                <DialogContent>
                    {resetStep === 'request' ? (
                        <Box sx={{ pt: 1 }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Enter your email address to receive a password reset pass-phrase.
                            </Typography>
                            <TextField
                                label="Email Address"
                                type="email"
                                value={resetEmail}
                                onChange={e => setResetEmail(e.target.value)}
                                required
                                fullWidth
                                autoFocus
                                variant="outlined"
                            />
                        </Box>
                    ) : (
                        <Box sx={{ pt: 1 }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Enter the pass-phrase from your email and your new password.
                            </Typography>
                            <TextField
                                label="Pass-phrase"
                                value={passPhrase}
                                onChange={e => setPassPhrase(e.target.value)}
                                required
                                fullWidth
                                autoFocus
                                margin="normal"
                                variant="outlined"
                            />
                            <TextField
                                label="New Password"
                                type="password"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                required
                                fullWidth
                                margin="normal"
                                variant="outlined"
                            />
                            <TextField
                                label="Confirm New Password"
                                type="password"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                required
                                fullWidth
                                margin="normal"
                                variant="outlined"
                            />
                        </Box>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={handleCloseResetDialog}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    {resetStep === 'request' ? (
                        <Button
                            variant="contained"
                            onClick={handleRequestPasswordReset}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handlePasswordReset}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Resetting...' : 'Reset Password'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LoginPage;
